import { useMemo, useState } from "react";
import {
  Search,
  Plus,
  Bot,
  Filter,
  ChevronDown,
  CheckCircle2,
  PowerOff,
} from "lucide-react";
import { cn } from "../../../../utils/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/UI/Popover";
import {
  eventTypeGradient,
  eventTypeIcon,
  isTagEvent,
  isGroupEvent,
} from "../../utils/eventType";

const STATUS_FILTERS = [
  { value: "all", label: "Tất cả" },
  { value: "active", label: "Đang hoạt động" },
  { value: "inactive", label: "Đã tắt" },
];

/**
 * Left sidebar showing the list of bots. 380px fixed width.
 * Search + status filter + event-type filter + bot cards (selectable).
 */
const BotListSidebar = ({
  bots = [],
  selectedId,
  onSelect,
  onCreateNew,
  eventTypes = [],
  shopsList = [],
  chatbotType = "event",
  loading = false,
}) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [eventTypeFilter, setEventTypeFilter] = useState("all");

  const eventTypeOptions = useMemo(() => {
    const opts = [{ value: "all", label: "Tất cả loại sự kiện" }];
    eventTypes.forEach((et) => {
      if (et.action) opts.push({ value: et.action, label: et.actionName || et.action });
    });
    return opts;
  }, [eventTypes]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return bots.filter((bot) => {
      if (statusFilter === "active" && !bot.isActive) return false;
      if (statusFilter === "inactive" && bot.isActive) return false;
      if (eventTypeFilter !== "all" && bot.eventType !== eventTypeFilter)
        return false;
      if (!q) return true;
      const haystack = [
        bot.description,
        bot.contentReply,
        bot.eventType,
        ...(Array.isArray(bot.conditionValue) ? bot.conditionValue : []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [bots, search, statusFilter, eventTypeFilter]);

  const title = chatbotType === "keyword" ? "ChatBot - QA" : "ChatBot - Sự kiện";
  const subtitle =
    chatbotType === "keyword"
      ? "Trả lời tự động theo từ khóa"
      : "Tự động hóa theo sự kiện";

  return (
    <aside className="w-[380px] shrink-0 bg-white border-r border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-sm">
          <Bot size={20} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900 truncate">
            {title}
          </div>
          <div className="text-[11px] text-gray-500 truncate">
            {subtitle} · {bots.length} bot
          </div>
        </div>
      </div>

      {/* Search + filters */}
      <div className="p-3 space-y-2 border-b border-gray-100">
        <div className="relative">
          <Search
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tìm bot theo mô tả, nội dung..."
            className="w-full pl-8 pr-3 py-2 text-sm rounded-md border border-gray-200 bg-gray-50 focus:bg-white focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 outline-none transition"
          />
        </div>

        <div className="flex items-center gap-2">
          <FilterDropdown
            label={
              STATUS_FILTERS.find((s) => s.value === statusFilter)?.label
            }
            value={statusFilter}
            options={STATUS_FILTERS}
            onChange={setStatusFilter}
          />
          <FilterDropdown
            label={
              eventTypeOptions.find((s) => s.value === eventTypeFilter)
                ?.label || "Loại sự kiện"
            }
            value={eventTypeFilter}
            options={eventTypeOptions}
            onChange={setEventTypeFilter}
            wide
          />
        </div>

        {(statusFilter !== "all" || eventTypeFilter !== "all" || search) && (
          <div className="text-[11px] text-gray-500 flex items-center justify-between">
            <span>{filtered.length} kết quả</span>
            <button
              type="button"
              className="text-indigo-600 hover:underline"
              onClick={() => {
                setSearch("");
                setStatusFilter("all");
                setEventTypeFilter("all");
              }}
            >
              Xóa bộ lọc
            </button>
          </div>
        )}
      </div>

      {/* Bot list */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
        {loading ? (
          <SkeletonList />
        ) : filtered.length === 0 ? (
          <EmptyState hasFilter={search || statusFilter !== "all" || eventTypeFilter !== "all"} />
        ) : (
          filtered.map((bot) => (
            <BotCard
              key={bot.id}
              bot={bot}
              selected={bot.id === selectedId}
              onClick={() => onSelect(bot.id)}
              eventTypes={eventTypes}
              shopsList={shopsList}
            />
          ))
        )}
      </div>

      {/* Sticky bottom: Add button */}
      <div className="p-3 border-t border-gray-100 bg-white">
        <button
          type="button"
          onClick={onCreateNew}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors shadow-sm"
        >
          <Plus size={16} />
          Thêm bot mới
        </button>
      </div>
    </aside>
  );
};

const BotCard = ({ bot, selected, onClick, eventTypes, shopsList }) => {
  const Icon = eventTypeIcon(bot.eventType);
  const gradient = eventTypeGradient(bot.eventType);
  const eventTypeName =
    eventTypes.find((e) => e.action === bot.eventType)?.actionName ||
    bot.eventType ||
    "—";
  const conditionPreview = useMemo(() => {
    if (!Array.isArray(bot.conditionValue) || bot.conditionValue.length === 0)
      return null;
    if (isTagEvent(bot.eventType)) {
      return `tag: ${bot.conditionValue.slice(0, 2).join(", ")}${bot.conditionValue.length > 2 ? "…" : ""}`;
    }
    if (isGroupEvent(bot.eventType)) {
      return `${bot.conditionValue.length} nhóm`;
    }
    return bot.conditionValue.slice(0, 2).join(", ");
  }, [bot.conditionValue, bot.eventType]);

  const shopCount = Array.isArray(bot.shopIds) ? bot.shopIds.length : 0;
  const shopText =
    shopCount === 0
      ? "Chưa chọn Zalo"
      : shopCount === 1
        ? shopsList.find((s) => s.id === bot.shopIds[0])?.name || "1 Zalo"
        : `${shopCount} Zalo`;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full text-left p-2.5 rounded-lg border transition-all relative group",
        selected
          ? "bg-indigo-50 border-indigo-200 shadow-sm"
          : "bg-white border-gray-100 hover:border-gray-200 hover:bg-gray-50",
      )}
    >
      {selected && (
        <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r-md bg-indigo-500" />
      )}
      <div className="flex items-start gap-2.5">
        <div
          className={cn(
            "w-9 h-9 shrink-0 rounded-lg bg-gradient-to-br flex items-center justify-center shadow-sm",
            gradient,
          )}
        >
          <Icon size={16} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="text-sm font-medium text-gray-900 truncate flex-1">
              {bot.description || (
                <span className="italic text-gray-400">(Chưa có mô tả)</span>
              )}
            </div>
            <StatusPill active={bot.isActive} />
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5 truncate">
            {eventTypeName}
            {conditionPreview ? ` · ${conditionPreview}` : ""}
          </div>
          <div className="text-[11px] text-gray-400 mt-0.5 truncate">
            {shopText}
          </div>
        </div>
      </div>
    </button>
  );
};

const StatusPill = ({ active }) => {
  if (active) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 px-1.5 py-0.5 rounded-full shrink-0">
        <CheckCircle2 size={10} />
        Bật
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-500 bg-gray-100 ring-1 ring-gray-200 px-1.5 py-0.5 rounded-full shrink-0">
      <PowerOff size={10} />
      Tắt
    </span>
  );
};

const FilterDropdown = ({ label, value, options, onChange, wide }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        className={cn(
          "inline-flex items-center justify-between gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 transition",
          wide ? "flex-1 min-w-0" : "",
        )}
      >
        <span className="flex items-center gap-1 min-w-0">
          <Filter size={12} className="shrink-0 text-gray-400" />
          <span className="truncate">{label}</span>
        </span>
        <ChevronDown size={12} className="shrink-0 text-gray-400" />
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-56 p-1" align="start">
      <div className="space-y-0.5">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              "w-full text-left px-2.5 py-1.5 text-sm rounded-md transition-colors",
              opt.value === value
                ? "bg-indigo-50 text-indigo-700 font-medium"
                : "hover:bg-gray-50 text-gray-700",
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </PopoverContent>
  </Popover>
);

const SkeletonList = () => (
  <div className="space-y-1.5">
    {Array.from({ length: 6 }).map((_, i) => (
      <div
        key={i}
        className="h-16 rounded-lg bg-gray-100 animate-pulse border border-gray-100"
      />
    ))}
  </div>
);

const EmptyState = ({ hasFilter }) => (
  <div className="text-center px-4 py-12">
    <div className="w-12 h-12 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-3">
      <Bot size={20} className="text-gray-400" />
    </div>
    <div className="text-sm text-gray-500">
      {hasFilter
        ? "Không có bot nào khớp với bộ lọc."
        : "Chưa có bot nào. Tạo bot đầu tiên để tự động tương tác với khách hàng."}
    </div>
  </div>
);

export default BotListSidebar;
